import React, { useState, useRef } from 'react';
import {
  Box, Button, Typography, Card, CardContent, CardHeader, TextField, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import TagEditor from '../Widgets/TagEditor';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useTranslation } from 'react-i18next';

const ActionsSimpleJavascript = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  actionType = 'simplejavascript',
  actionTitle,
  defaultOutputTag = 'js_result',
  defaultOutputDescription = 'JavaScript execution result'
}) => {
  const { t } = useTranslation();
  const [jsCode, setJsCode] = useState('');
  const [outputTag, setOutputTag] = useState(defaultOutputTag);
  const [outputDescription, setOutputDescription] = useState(defaultOutputDescription);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const jsCodeRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    customComparison: (initial) => {
      return jsCode === (initial.jsCode || '') &&
             outputTag === (initial.outputTag || defaultOutputTag) &&
             outputDescription === (initial.outputDescription || defaultOutputDescription);
    },
    customSaveData: () => ({
      jsCode,
      outputTag,
      outputDescription
    }),
    loadDataFunction: (data) => {
      let customData = data.customData || {};
      if (customData.jsCode !== undefined) {
        setJsCode(customData.jsCode);
      }
      if (customData.outputTag !== undefined) {
        setOutputTag(customData.outputTag);
      }
      if (customData.outputDescription !== undefined) {
        setOutputDescription(customData.outputDescription);
      }
    }
  });

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'jsCode') {
      jsCodeRef.current?.insertTag(value);
    } else if (menuType === 'outputTag') {
      setOutputTag(value);
    }
    handleMenuClose();
  };

  const resolvedActionTitle =
    actionTitle ||
    t('workflow.simple_javascript', 'Simple JavaScript');

  return (
    <ActionSettingsLayout
      title={resolvedActionTitle}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      tagsDropdownProps={{
        tags: tags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
        menuType: menuType
      }}
    >
      {/* JavaScript Code */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                <CodeIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                CODE
              </Box>
              {t('workflow.js_code_title', 'JavaScript Code (with tag support)')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{
            border: '1px solid #d0d0e8',
            borderRadius: '8px',
            mb: 2,
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
            },
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          }}>
            <TagEditor
                    caseid={caseid}
              ref={jsCodeRef}
              value={jsCode}
              onChange={setJsCode}
              multiline
              rows={8}
              placeholder="// Enter your JavaScript code here
// Use tags like {filename}, {textextract}, etc.
// Example: const text = '{textextract}'; 
// return text.length;"
              onTagClick={(e) => handleMenuOpen(e, 'jsCode')}
              tags={tags}
            />
          </Box>

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            p: 2,
            backgroundColor: '#f8f9ff',
            borderRadius: '8px',
            border: '1px solid #eaeaf0',
          }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#5757b0',
              fontSize: '0.85rem',
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              mb: 1,
            }}>
              {t('quick_tags', 'Quick Tags')}:
            </Typography>
            {[
              { label: 'Filename', value: 'filename' },
              { label: 'Text Extract', value: 'textextract' },
              { label: 'Summary', value: 'summary' },
              { label: 'File ID', value: 'fileid' },
            ].map(tag => (
              <Chip
                key={tag.value}
                label={tag.label}
                onClick={() => {
                  if (jsCodeRef.current) {
                    jsCodeRef.current.focus();
                    setTimeout(() => {
                      jsCodeRef.current.insertTag(tag.value);
                    }, 10);
                  }
                }}
                size="small"
                sx={{
                  backgroundColor: '#f0f0ff',
                  color: '#5757b0',
                  borderColor: '#5757b030',
                  border: '1px solid',
                  '&:hover': {
                    backgroundColor: '#e0e0ff',
                    boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                  },
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>

          <Box sx={{
            p: 2,
            backgroundColor: '#fff9e6',
            borderRadius: '8px',
            border: '1px solid #ffe066',
            mb: 2,
          }}>
            <Typography sx={{
              fontSize: '0.85rem',
              color: '#856404',
              fontWeight: 500,
              mb: 1,
            }}>
              💡 Tips:
            </Typography>
            <Typography sx={{
              fontSize: '0.8rem',
              color: '#856404',
              lineHeight: '1.6',
            }}>
              • Use <code style={{ backgroundColor: '#ffeecc', padding: '2px 4px', borderRadius: '3px' }}>return</code> to specify the result value
              <br />
              • Tags will be replaced before execution
              <br />
              • JavaScript executes in a Playwright Chromium browser (full ES6+ support)
              <br />
              • Async/await is supported - your code runs in an async context
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Output Configuration */}
      <Card sx={{
        mb: 0,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                OUTPUT
              </Box>
              {t('workflow.result_config', 'Result Configuration')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#444',
              fontSize: '0.9rem',
              mb: 1,
            }}>
              {t('workflow.output_tag_name', 'Output Tag Name')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                value={outputTag}
                onChange={(e) => setOutputTag(e.target.value)}
                placeholder="e.g., js_result, calculated_value, processed_text"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    '&:hover fieldset': {
                      borderColor: '#5757b0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5757b0',
                    },
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => handleMenuOpen(e, 'outputTag')}
                startIcon={<AddIcon />}
                sx={{
                  color: '#5757b0',
                  borderColor: '#5757b0',
                  fontWeight: 600,
                  minWidth: 'auto',
                  padding: '6px 12px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#f0f0ff',
                    borderColor: '#5757b0',
                  },
                  transition: 'all 0.2s ease',
                  textTransform: 'none',
                }}
              >
                {t('workflow.select_tag', 'Select Tag')}
              </Button>
            </Box>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#666',
              mt: 0.5,
              fontStyle: 'italic',
            }}>
              {t('workflow.js_result_help', 'The JavaScript result will be saved to this tag')}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{
              fontWeight: 600,
              color: '#444',
              fontSize: '0.9rem',
              mb: 1,
            }}>
              {t('workflow.output_tag_description', 'Output Tag Description (Optional)')}
            </Typography>
            <TextField
              fullWidth
              value={outputDescription}
              onChange={(e) => setOutputDescription(e.target.value)}
              placeholder="e.g., Calculated value from JavaScript"
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#5757b0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5757b0',
                  },
                },
              }}
            />
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#666',
              mt: 0.5,
              fontStyle: 'italic',
            }}>
              {t('workflow.output_description_help', 'A description to help identify this tag')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsSimpleJavascript);
